import ReactECharts from 'echarts-for-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { LoadingState } from '../common';
import { useEventContext } from '../../contexts/EventContext';
import { useCumulativeAnalysisQuery } from '../../hooks/useCumulativeAnalysisQuery';
import { useEventCollectFlagsQuery } from '../../hooks/useEventCollectFlagsQuery';
import { useGraphCategoriesQuery } from '../../hooks/useGraphCategoriesQuery';
import { useSplitDistributionQuery } from '../../hooks/useSplitDistributionQuery';
import { useStoredRefreshInterval } from '../../hooks/useStoredRefreshInterval';
import { EVENT_LIST_ROUTE_PATH } from '../../routes/appRoutes';
import { createCumulativeAnalysisOption } from './cumulativeAnalysisChartOptions';
import { StatisticsRefreshControl } from './StatisticsRefreshControl';
import StatisticsFilterBar, {
  emptyStatisticsFilters,
  toGraphFilters,
  uniqueFilterValues,
} from './StatisticsFilterBar';

const refreshOptions = [
  { label: '수동', value: 0 },
  { label: '10초', value: 10000 },
  { label: '30초', value: 30000 },
  { label: '1분', value: 60000 },
];
const CUMULATIVE_REFRESH_STORAGE_KEY = 'statistics.cumulative.refreshIntervalMs';

function CumulativeAnalysisDashboard() {
  const { selectedEventId } = useEventContext();
  const collectFlagsQuery = useEventCollectFlagsQuery(selectedEventId);
  const collectBirth = collectFlagsQuery.data?.collectBirth ?? true;
  const collectGender = collectFlagsQuery.data?.collectGender ?? true;
  const [filters, setFilters] = useState(emptyStatisticsFilters);
  // 백엔드 응답에 남아있는 값과 무관하게, 미수집 항목은 필터를 무조건 전체로 고정한다.
  const graphFilters = useMemo(() => {
    const base = toGraphFilters(filters);
    return {
      ...base,
      ageGroup: collectBirth ? base.ageGroup : '',
      gender: collectGender ? base.gender : '',
    };
  }, [filters, collectBirth, collectGender]);
  const {
    applyRefreshInterval,
    draftRefreshIntervalMs,
    refreshIntervalMs,
    setDraftRefreshIntervalMs,
  } = useStoredRefreshInterval(CUMULATIVE_REFRESH_STORAGE_KEY);
  const categoryQuery = useGraphCategoriesQuery(selectedEventId);
  const filterOptionQuery = useSplitDistributionQuery(selectedEventId);
  const categories = useMemo(() => categoryQuery.data ?? [], [categoryQuery.data]);
  const filterOptionParticipants = useMemo(
    () => filterOptionQuery.data?.participants ?? [],
    [filterOptionQuery.data?.participants]
  );
  const genderOptions = useMemo(
    () => uniqueFilterValues(filterOptionParticipants.map((participant) => participant.gender)),
    [filterOptionParticipants]
  );
  const ageGroupOptions = useMemo(
    () => uniqueFilterValues(filterOptionParticipants.map((participant) => participant.ageGroup)),
    [filterOptionParticipants]
  );
  const selectedCategory = useMemo(
    () => categories.find((category) => String(category.id) === filters.categoryId) ?? null,
    [categories, filters.categoryId]
  );
  const canLoadChart = categoryQuery.isSuccess;
  const cumulativeQuery = useCumulativeAnalysisQuery(
    selectedEventId,
    selectedCategory,
    refreshIntervalMs,
    canLoadChart,
    graphFilters
  );
  const charts = useMemo(() => cumulativeQuery.data?.charts ?? [], [cumulativeQuery.data?.charts]);
  const hasChartData = charts.some((chart) => chart.splits.length > 0);
  const updatedAt = Math.max(categoryQuery.dataUpdatedAt, cumulativeQuery.dataUpdatedAt);
  const isPageLoading = categoryQuery.isLoading || cumulativeQuery.isLoading;
  const isPageFetching = categoryQuery.isFetching || cumulativeQuery.isFetching;
  const pageError = categoryQuery.error ?? cumulativeQuery.error;
  const isPageError = categoryQuery.isError || cumulativeQuery.isError;
  const errorMessage = pageError instanceof Error
    ? pageError.message
    : '누적 참가자 구간 통과 통계 데이터를 불러오지 못했습니다.';

  useEffect(() => {
    const hasSelectedCategory = categories.some(
      (category) => String(category.id) === filters.categoryId
    );

    if (filters.categoryId && !hasSelectedCategory) {
      setFilters((current) => ({ ...current, categoryId: '', group: '' }));
    }
  }, [categories, filters.categoryId]);

  let chartContent: ReactNode = (
    <div className="cumulative-analysis-card-list" aria-busy={isPageFetching}>
      {charts.map((chart) => (
        <section className="cumulative-analysis-card" key={chart.category}>
          <h2>{chart.category}</h2>
          <ReactECharts
            option={createCumulativeAnalysisOption(chart, collectGender)}
            style={{ height: 520 }}
          />
        </section>
      ))}
    </div>
  );

  if (selectedEventId == null) {
    chartContent = (
      <div className="cumulative-analysis-message">
        선택된 대회가 없습니다. <Link to={EVENT_LIST_ROUTE_PATH}>대회 목록에서 선택</Link>해 주세요.
      </div>
    );
  } else if (isPageLoading) {
    chartContent = <LoadingState message="데이터를 불러오는 중입니다." />;
  } else if (categories.length === 0) {
    chartContent = (
      <div className="cumulative-analysis-message">선택 가능한 종목이 없습니다.</div>
    );
  } else if (!hasChartData) {
    chartContent = (
      <div className="cumulative-analysis-message">
        표시할 누적 참가자 구간 통과 통계 데이터가 없습니다.
      </div>
    );
  }

  return (
    <div className="cumulative-analysis-page">
      <div className="cumulative-analysis-header">
        <div className="page-title-block">
          <p className="page-section">Statistics</p>
          <h1>누적 참가자 구간 통과 통계</h1>
          <p className="page-description">선택한 종목의 구간별 누적 통과 현황을 확인합니다.</p>
        </div>
        <StatisticsRefreshControl
          ariaLabel="누적 참가자 구간 통과 통계 갱신 주기"
          className="cumulative-refresh-control"
          draftRefreshIntervalMs={draftRefreshIntervalMs}
          isApplyDisabled={draftRefreshIntervalMs === refreshIntervalMs}
          isRefreshDisabled={isPageFetching}
          options={refreshOptions}
          updatedAt={updatedAt}
          onApply={applyRefreshInterval}
          onChange={setDraftRefreshIntervalMs}
          onRefresh={() => {
            void Promise.all([
              categoryQuery.refetch(),
              cumulativeQuery.refetch(),
            ]);
          }}
        />
      </div>

      <StatisticsFilterBar
        filters={filters}
        categories={categories}
        genderOptions={genderOptions}
        ageGroupOptions={ageGroupOptions}
        disabled={isPageFetching}
        collectBirth={collectBirth}
        collectGender={collectGender}
        onChange={setFilters}
        onReset={() => setFilters(emptyStatisticsFilters)}
      />

      {isPageError && (
        <div className="cumulative-analysis-message cumulative-analysis-error" role="alert">
          {errorMessage}
        </div>
      )}

      {chartContent}
    </div>
  );
}

export default CumulativeAnalysisDashboard;
